import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Apply from './pages/Apply';
import Eligibility from './pages/Eligibility';
import LocalSponsor from './pages/LocalSponsor';
import About from './pages/About';
import Faq from './pages/Faq';
import Contact from './pages/Contact';
import Affiliates from './pages/Affiliates';
import Requirements from './pages/Requirements';
import CountryDetail from './pages/CountryDetail';
import Track from './pages/Track';
import ApplySuccess from './pages/ApplySuccess';
import SingaporeVisaFor from './pages/SingaporeVisaFor';
import SingaporeVisaHub from './pages/SingaporeVisaHub';
import SingaporeVisaCountries from './pages/SingaporeVisaCountries';
import SingaporeVisaGuide from './pages/SingaporeVisaGuide';
import NotFound from './pages/NotFound';
import RefundPolicy from './pages/RefundPolicy';
import Terms from './pages/Terms';
import AdminLogin from './pages/AdminLogin';
import AdminChangePassword from './pages/AdminChangePassword';
import AdminForgotPassword from './pages/AdminForgotPassword';
import AdminResetPassword from './pages/AdminResetPassword';
import AdminDashboard from './pages/AdminDashboard';
import TemporaryUnavailable from './pages/TemporaryUnavailable';
import { Toaster } from './components/ui/toaster';
import { MessageCircle } from 'lucide-react';
import { CmsProvider, useCms } from './lib/CmsContext';
import { SEO_GUIDE_BY_PATH, SEO_COUNTRY_BY_SLUG } from './data/seo-content';
import useGaPageview from './lib/useGaPageview';

const SITE_URL = 'https://visasg.com';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

/**
 * SPA route tracker for Google Analytics 4. Wraps the useGaPageview
 * hook in a null-rendering component so we can drop it inside
 * <BrowserRouter> once and let it fire a sanitised page_view on every
 * client-side navigation (see /src/lib/useGaPageview.js).
 */
function GaRouteTracker() {
  useGaPageview();
  return null;
}

function WhatsAppFloat() {
  const { company } = useCms();
  return (
    <a href={`https://wa.me/${company.whatsappRaw}`} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp"
      className="fixed bottom-5 right-5 z-30 h-12 w-12 md:h-14 md:w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg grid place-items-center">
      <MessageCircle size={22} />
    </a>
  );
}

/**
 * Site-wide JSON-LD (Organization + WebSite). Injected once at the app
 * root so every rendered page includes it in its <head>, and every
 * pre-rendered static HTML file inherits it too.
 */
function GlobalSchema() {
  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'VisaSG',
    alternateName: 'WAAN TRAVEL PTE. LTD.',
    url: SITE_URL,
    logo: `${SITE_URL}/brand/logo.png`,
    sameAs: [],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Singapore',
      addressCountry: 'SG',
    },
    identifier: 'TA03795',
    description:
      'Singapore-licensed travel agent providing online Singapore eVisa application preparation and processing (STB Travel Agent Licence TA03795).',
  };
  const siteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'VisaSG',
    url: SITE_URL,
    inLanguage: 'en',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/singapore-visa/countries/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <script type="application/ld+json">{JSON.stringify(siteLd)}</script>
    </Helmet>
  );
}

/**
 * Route-level noindex for private / customer / admin surfaces so Google
 * never indexes them even if they leak into search results. This is a
 * belt-and-braces layer on top of sitemap.xml (which already excludes
 * these paths) and robots.txt.
 */
function NoIndex() {
  return (
    <Helmet>
      <meta name="robots" content="noindex,follow" />
    </Helmet>
  );
}

/**
 * Client-side redirect helper. Emits both a `<Navigate replace>` for
 * users and a hint for crawlers via a canonical + robots noindex on the
 * short-lived shell. In production, the pre-rendered static HTML for
 * legacy URLs (see scripts/generate-seo-pages.js) carries a
 * <meta http-equiv="refresh" content="0; url=..."> which Google treats
 * as a 301-equivalent consolidation.
 */
function RedirectTo({ to }) {
  return (
    <>
      <Helmet>
        <link rel="canonical" href={`${SITE_URL}${to}`} />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Navigate to={to} replace />
    </>
  );
}

// Single dispatcher for `/singapore-visa/:segment(/)?` - resolves to:
//   - Countries directory if segment === "countries"
//   - Guide page if the segment matches SEO_GUIDE_BY_PATH
//   - Country page if the segment matches SEO_COUNTRY_BY_SLUG
//   - 404 otherwise
function SingaporeVisaSegmentRouter() {
  const { segment } = useParams();
  if (!segment) return <NotFound />;
  if (segment === 'countries') return <SingaporeVisaCountries />;
  if (SEO_GUIDE_BY_PATH[segment]) return <SingaporeVisaGuide />;
  if (SEO_COUNTRY_BY_SLUG[segment]) return <SingaporeVisaFor />;
  return <NotFound />;
}

function PublicLayout({ children }) {
  return (
    <CmsProvider>
      <Header />
      <main>{children}</main>
      <Footer />
      <WhatsAppFloat />
    </CmsProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <GaRouteTracker />
          <GlobalSchema />
          <Routes>
            {/* Admin routes — MUST remain fully functional. Do not touch. */}
            <Route path="/admin/login" element={<><NoIndex /><AdminLogin /></>} />
            <Route path="/admin/forgot-password" element={<><NoIndex /><AdminForgotPassword /></>} />
            <Route path="/admin/reset-password" element={<><NoIndex /><AdminResetPassword /></>} />
            <Route path="/admin/change-password" element={<><NoIndex /><AdminChangePassword /></>} />
            <Route path="/admin" element={<><NoIndex /><AdminDashboard /></>} />

            {/*
              PUBLIC SITE TEMPORARILY OFFLINE.
              Every non-admin route renders the temporary-unavailable
              notice instead of the previous public pages. Existing
              public routes and page components are intentionally NOT
              deleted — they remain in the codebase and can be
              re-enabled by restoring the previous <Route> block below.
              Admin routes above are unaffected.
            */}
            <Route path="*" element={<><NoIndex /><TemporaryUnavailable /></>} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
